import React from 'react'
import { useTranslation } from 'react-i18next'
import { LegendDot, surface } from '../ui/surface'

const VISIBLE_ROWS = 5

const formatTraffic = (value) => {
  if (value == null) return '—'
  if (value >= 1000) return `${(value / 1024).toFixed(1)} Гб`
  return `${Math.round(value)} Мб`
}

const TrafficBar = ({ value, max, barClass }) => {
  const pct = max > 0 && value > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0
  return (
    <div className="flex items-center gap-3">
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/[0.05]">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-16 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-300">
        {formatTraffic(value)}
      </span>
    </div>
  )
}

const TopConsumersTable = ({ rows, onViewAll }) => {
  const { t } = useTranslation()
  const visible = rows.slice(0, VISIBLE_ROWS)
  const max = Math.max(0, ...visible.flatMap((row) => [Number(row.download) || 0, Number(row.upload) || 0]))

  return (
    <section className={`flex h-full flex-col gap-5 rounded-3xl ${surface.section} p-6`}>
      <header>
        <h3 className="font-display text-lg text-white">{t('topConsumers.title')}</h3>
        <p className="mt-1 text-sm text-muted-400">{t('topConsumers.subtitle')}</p>
        <div className="mt-3 flex items-center gap-4">
          <LegendDot color="bg-accent-cyan/60">Download</LegendDot>
          <LegendDot color="bg-accent-pink/60">Upload</LegendDot>
        </div>
      </header>

      {visible.length ? (
        <ol className="flex flex-col gap-2">
          {visible.map((row, index) => (
            <li key={`${row.school}-${index}`} className={`rounded-2xl ${surface.card} px-4 py-3`}>
              <div className="flex items-center gap-3">
                <span className="w-5 shrink-0 font-display text-sm tabular-nums text-muted-500">{index + 1}</span>
                <p className="min-w-0 flex-1 truncate text-sm font-medium text-white" title={row.school}>
                  {row.school}
                </p>
              </div>
              <div className="mt-2.5 flex flex-col gap-1.5 pl-8">
                <TrafficBar value={row.download} max={max} barClass="bg-accent-cyan/60" />
                <TrafficBar value={row.upload} max={max} barClass="bg-accent-pink/60" />
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="py-6 text-center text-sm text-muted-500">{t('topConsumers.noData')}</p>
      )}

      {visible.length > 0 && onViewAll && (
        <button
          type="button"
          onClick={onViewAll}
          className="mt-auto inline-flex items-center gap-1.5 self-end text-sm text-muted-400 transition hover:text-white"
        >
          {t('topConsumers.viewAll')}
          <span className="material-icons text-sm">arrow_forward</span>
        </button>
      )}
    </section>
  )
}

export default TopConsumersTable
